import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Ticket, Plus, Copy, Ban, RefreshCw, Gift, Trash2 } from 'lucide-react';
import { api } from '@/api';

export function TierOverridesPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ email: '', tier: 'pro', expires_at: '', note: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/admin/tier-overrides'); setItems(data.items || []); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load complimentary access'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put('/admin/tier-overrides', { email: form.email, tier: form.tier, expires_at: form.expires_at || null, note: form.note || null });
      toast.success(`${form.email} now has complimentary ${form.tier} access.`);
      setForm({ email: '', tier: 'pro', expires_at: '', note: '' });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Could not grant access'); }
    finally { setBusy(false); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Remove complimentary access for ${o.email}? They drop to whatever they pay for (or Guest).`)) return;
    try { await api.delete(`/admin/tier-overrides/${encodeURIComponent(o.email)}`); toast.success('Access removed.'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Remove failed'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-tier-overrides">
      <div>
        <div className="mi-eyebrow text-[#E63946] flex items-center gap-2"><Gift className="w-3 h-3" /> // COMPLIMENTARY ACCESS · NO STRIPE NEEDED</div>
        <div className="text-white/60 text-xs mt-1">Comp a specific partner Starter, Pro or Fleet by email. Applies the moment they sign in with that address; optional end date.</div>
      </div>
      <form onSubmit={save} className="border-2 border-[#E63946]/40 bg-[#E63946]/[0.04] p-5 grid md:grid-cols-[1fr_130px_170px_1fr_auto] gap-3 items-end" data-testid="admin-override-form">
        <L label="Partner email"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="partner@shop.com" data-testid="admin-override-email" className="mi-input" /></L>
        <L label="Tier">
          <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} data-testid="admin-override-tier" className="mi-input">
            <option value="starter">Starter</option><option value="pro">Pro</option><option value="fleet">Fleet</option>
          </select>
        </L>
        <L label="Ends (optional)"><input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="admin-override-expires" className="mi-input" /></L>
        <L label="Note (optional)"><input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Founding partner" className="mi-input" /></L>
        <button type="submit" disabled={busy} data-testid="admin-override-save" className="mi-btn-red text-xs whitespace-nowrap"><Gift className="w-3.5 h-3.5" /> {busy ? 'Saving…' : 'Grant access'}</button>
      </form>
      {items.length > 0 && (
        <div className="border mi-line-strong overflow-x-auto">
          <table className="w-full text-xs" data-testid="admin-override-table">
            <thead><tr className="bg-black/60">{['EMAIL', 'TIER', 'STATE', 'ENDS', 'NOTE', 'GRANTED BY', ''].map((h) => <th key={h} className="p-3 text-left mi-eyebrow text-white/50 text-[9px] whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>
              {items.map((o) => (
                <tr key={o.email} className="border-t mi-line hover:bg-white/[0.02]" data-testid={`override-row-${o.email}`}>
                  <td className="p-3 text-white font-mono">{o.email}</td>
                  <td className="p-3 text-white/80 capitalize">{o.tier}</td>
                  <td className="p-3"><span className={`mi-tag text-[8px] ${o.active ? 'mi-tag-red' : 'border-white/15 text-white/35'}`}>{o.active ? 'ACTIVE' : 'EXPIRED'}</span></td>
                  <td className="p-3 font-mono text-white/60">{o.expires_at ? new Date(o.expires_at).toLocaleDateString() : 'never'}</td>
                  <td className="p-3 text-white/50 max-w-[200px] truncate">{o.note || ''}</td>
                  <td className="p-3 text-white/40">{o.granted_by}</td>
                  <td className="p-3"><button onClick={() => remove(o)} data-testid={`override-remove-${o.email}`} className="text-white/50 hover:text-[#E63946] p-1" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const STATE_STYLE = {
  unused:  'border-white/30 text-white/80',
  active:  'mi-tag-red',
  expired: 'border-yellow-400/50 text-yellow-400',
  revoked: 'border-white/15 text-white/35 line-through',
};

export default function TrialPassesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ days: 30, tier: 'fleet', intended_for: '', note: '' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/trial-passes'); setItems(data.items || []); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load passes'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post('/admin/trial-passes', {
        days: +form.days, tier: form.tier,
        intended_for: form.intended_for || null, note: form.note || null,
      });
      await navigator.clipboard?.writeText(data.code).catch(() => {});
      toast.success(`Pass ${data.code} created — copied to clipboard.`);
      setForm({ ...form, intended_for: '', note: '' });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Could not create pass'); }
    finally { setCreating(false); }
  };

  const revoke = async (p) => {
    if (!window.confirm(`Revoke ${p.code}?${p.redeemed_by ? ` This ends ${p.redeemed_by}'s trial immediately.` : ''}`)) return;
    try { await api.delete(`/admin/trial-passes/${p.code}`); toast.success('Pass revoked.'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Revoke failed'); }
  };

  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success('Code copied.'); };
  const counts = items.reduce((a, p) => ({ ...a, [p.state]: (a[p.state] || 0) + 1 }), {});

  return (
    <div className="space-y-10" data-testid="admin-trial-passes">
      <TierOverridesPanel />
      <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mi-eyebrow text-[#E63946] flex items-center gap-2"><Ticket className="w-3 h-3" /> // TRIAL PASSES · ONE-TIME 30-DAY FULL ACCESS</div>
          <div className="text-white/60 text-xs mt-1">
            Hand a code to a prospective partner. They sign in, redeem it once, and get full Wrapulator access for the trial period —
            then they're prompted to convert to a paid plan.
            {items.length > 0 && <> · {counts.unused || 0} unused · {counts.active || 0} active · {counts.expired || 0} expired · {counts.revoked || 0} revoked</>}
          </div>
        </div>
        <button onClick={load} className="mi-btn-ghost text-xs"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
      </div>

      <form onSubmit={create} className="border-2 border-[#E63946]/40 bg-[#E63946]/[0.04] p-5 grid md:grid-cols-[110px_130px_1fr_1fr_auto] gap-3 items-end" data-testid="admin-pass-form">
        <L label="Days"><input type="number" min={1} max={365} value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} data-testid="admin-pass-days" className="mi-input" /></L>
        <L label="Tier">
          <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} data-testid="admin-pass-tier" className="mi-input">
            <option value="fleet">Fleet (full)</option>
            <option value="pro">Pro</option>
            <option value="starter">Starter</option>
          </select>
        </L>
        <L label="Intended for (optional)"><input value={form.intended_for} onChange={(e) => setForm({ ...form, intended_for: e.target.value })} placeholder="prospect@shop.com" data-testid="admin-pass-for" className="mi-input" /></L>
        <L label="Note (optional)"><input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Met at SEMA booth" data-testid="admin-pass-note" className="mi-input" /></L>
        <button type="submit" disabled={creating} data-testid="admin-pass-create" className="mi-btn-red text-xs whitespace-nowrap"><Plus className="w-3.5 h-3.5" /> {creating ? 'Creating…' : 'Generate pass'}</button>
      </form>

      <div className="border mi-line-strong overflow-x-auto">
        <table className="w-full text-xs" data-testid="admin-pass-table">
          <thead>
            <tr className="bg-black/60">
              {['CODE', 'STATE', 'TIER · DAYS', 'INTENDED FOR', 'REDEEMED BY', 'EXPIRES', 'NOTE', ''].map((h) => (
                <th key={h} className="p-3 text-left mi-eyebrow text-white/50 text-[9px] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!items.length ? (
              <tr><td colSpan={8} className="p-6 text-white/50 text-center">{loading ? 'Loading…' : 'No passes yet — generate the first one above.'}</td></tr>
            ) : items.map((p) => (
              <tr key={p.code} className="border-t mi-line hover:bg-white/[0.02]" data-testid={`pass-row-${p.code}`}>
                <td className="p-3 font-mono text-white tracking-wider">
                  <button onClick={() => copy(p.code)} className="inline-flex items-center gap-1.5 hover:text-[#E63946]" title="Copy"><span data-testid={`pass-code-${p.code}`}>{p.code}</span> <Copy className="w-3 h-3 opacity-60" /></button>
                </td>
                <td className="p-3"><span className={`mi-tag text-[8px] ${STATE_STYLE[p.state]}`} data-testid={`pass-state-${p.code}`}>{p.state.toUpperCase()}</span></td>
                <td className="p-3 text-white/70 capitalize">{p.tier} · {p.days}d</td>
                <td className="p-3 text-white/60">{p.intended_for || <span className="text-white/25">—</span>}</td>
                <td className="p-3 text-white/80">{p.redeemed_by || <span className="text-white/25">—</span>}</td>
                <td className="p-3 font-mono text-white/60">{p.expires_at ? new Date(p.expires_at).toLocaleDateString() : <span className="text-white/25">—</span>}</td>
                <td className="p-3 text-white/50 max-w-[200px] truncate">{p.note || ''}</td>
                <td className="p-3">
                  {p.state !== 'revoked' && p.state !== 'expired' && (
                    <button onClick={() => revoke(p)} data-testid={`pass-revoke-${p.code}`} className="text-white/50 hover:text-[#E63946] p-1" title="Revoke"><Ban className="w-3.5 h-3.5" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return <label className="block"><div className="mi-eyebrow text-white/60 text-[10px] mb-1.5">{label}</div>{children}</label>;
}
